import { useState, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { normalizeUserRoleId, ROLE_IDS } from '../../utils/userRoles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  useTheme,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DescriptionIcon from '@mui/icons-material/Description';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import ProfileDialog from '../ProfileDialog/ProfileDialog';

export const DRAWER_WIDTH = 280;

// Color combo: slate sidebar + white header + warm content background
const SIDEBAR_BG = '#0f172a';           // slate-900 – deep navy slate
const SIDEBAR_BORDER = 'rgba(255,255,255,0.08)';
const SIDEBAR_TEXT = '#f1f5f9';
const SIDEBAR_TEXT_MUTED = 'rgba(241,245,249,0.75)';
const SIDEBAR_HOVER = 'rgba(255,255,255,0.06)';
const SIDEBAR_ACTIVE_BG = 'rgba(255,255,255,0.1)';
const SIDEBAR_ACTIVE_BORDER = '#38bdf8';  // sky-400 accent

const HEADER_BG = '#ffffff';
const HEADER_BORDER = '#e2e8f0';
const TEXT_PRIMARY = '#1e293b';
const USER_PILL_BG = '#f1f5f9';
const USER_PILL_HOVER = '#e2e8f0';

const CONTENT_BG = '#f8fafc';          // warm off-white for pages
const CONTENT_BG_PATTERN = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2394a3b8' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

const menuItems = [
  { label: 'Dashboard', path: '/admin', icon: <DashboardIcon /> },
  { label: 'Customers', path: '/admin/company-sites', icon: <PeopleIcon /> },
  { label: 'Equipment', path: '/admin/equipment', icon: <BuildIcon /> },
  { label: 'QR Codes', path: '/admin/qr-codes', icon: <QrCode2Icon /> },
  { label: 'Worklists', path: '/admin/worklists', icon: <PlaylistAddCheckIcon /> },
  { label: 'Certificates', path: '/admin/certificates', icon: <DescriptionIcon /> },
  { label: 'Notification Page', path: '/admin/notifications', icon: <NotificationsIcon /> },
  { label: 'Requests', path: '/admin/requests', icon: <RequestQuoteIcon /> },
  { label: 'Access Management', path: '/admin/role-access-management', icon: <LockIcon /> },
  { label: 'Settings', path: '/admin/settings', icon: <SettingsIcon /> },
];

function AdminLayout({ userData, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const sidebarOpen = true; // sidebar is always open (permanent)
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const profileAnchorRef = useRef(null);

  const roleId = normalizeUserRoleId(userData?.userDetails);

  const handleNav = (path) => navigate(path);
  const lastLoginTime = typeof window !== 'undefined' ? localStorage.getItem('lastLoginTime') : null;
  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate('/');
  };

  const isActive = (path) =>
    location.pathname === path || (path === '/admin' && location.pathname === '/admin');

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: CONTENT_BG }}>
      {/* Fixed header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { xs: 0, md: DRAWER_WIDTH },
          bgcolor: HEADER_BG,
          color: TEXT_PRIMARY,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          borderBottom: `1px solid ${HEADER_BORDER}`,
        }}
      >
        <Toolbar disableGutters sx={{ px: 3, minHeight: 72 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'inherit', display: { xs: 'none', sm: 'block' } }}>
            Absolute Safety
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Box
            ref={profileAnchorRef}
            onClick={() => setProfileDialogOpen(true)}
            sx={{ cursor: 'pointer', '&:hover .MuiAvatar-root': { opacity: 0.9 } }}
            aria-label="Open profile"
          >
            <Avatar sx={{ width: 38, height: 38, bgcolor: 'primary.main', color: '#fff' }}>
              <PersonIcon sx={{ fontSize: 22 }} />
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      <ProfileDialog
        userDetails={userData?.userDetails}
        isOpen={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        lastLoginTime={lastLoginTime}
        anchorElement={profileAnchorRef.current}
      />

      {/* Permanent sidenav – always open, no toggle */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            mt: 0,
            borderRight: '1px solid',
            borderColor: SIDEBAR_BORDER,
            bgcolor: SIDEBAR_BG,
            boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
            zIndex: 1100,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: SIDEBAR_BG }}>
          <Box sx={{ py: 2.5, px: 2.5, borderBottom: '1px solid', borderColor: SIDEBAR_BORDER }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', width: 44, height: 44 }}>
                <PersonIcon sx={{ color: SIDEBAR_TEXT, fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: SIDEBAR_TEXT }}>
                  {roleId === ROLE_IDS.SUPERADMIN ? 'Super Admin' : 'Admin'}
                </Typography>
               
              </Box>
            </Box>
          </Box>
          <List sx={{ flex: 1, pt: 2, px: 1.5, overflow: 'auto' }}>
            {menuItems.map(({ label, path, icon, badge }) => (
              <ListItemButton
                key={path}
                onClick={() => handleNav(path)}
                selected={isActive(path)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  py: 1.25,
                  px: 2,
                  color: SIDEBAR_TEXT_MUTED,
                  '&.Mui-selected': {
                    bgcolor: SIDEBAR_ACTIVE_BG,
                    color: SIDEBAR_TEXT,
                    fontWeight: 600,
                    borderLeft: '3px solid',
                    borderColor: SIDEBAR_ACTIVE_BORDER,
                    pl: 2,
                    ml: 0,
                  },
                  '&:hover': {
                    bgcolor: SIDEBAR_HOVER,
                    color: SIDEBAR_TEXT,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{icon}</ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.9375rem' }} />
                {badge != null && <Badge badgeContent={badge} color="primary" size="small" />}
              </ListItemButton>
            ))}
          </List>
          <List sx={{ px: 1.5, pb: 2, flexShrink: 0 }}>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                py: 1.25,
                px: 2,
                color: '#ef5350',
                '&:hover': { bgcolor: 'rgba(239,83,80,0.12)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: 500 }} />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      {/* Main content: full width; only top + left padding (for header + sidebar); no right/bottom so page fills */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          pt: { xs: 10, sm: 11 },
          pr: 0,
          pb: 0,
          pl: { xs: 2, md: sidebarOpen ? 3 : 2 },
          width: '100%',
          maxWidth: 'none',
          boxSizing: 'border-box',
          overflow: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: '#94a3b8 #f1f5f9',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { background: '#f1f5f9', borderRadius: 4 },
          '&::-webkit-scrollbar-thumb': { background: '#94a3b8', borderRadius: 4 },
          '&::-webkit-scrollbar-thumb:hover': { background: '#64748b' },
          bgcolor: CONTENT_BG,
          backgroundImage: CONTENT_BG_PATTERN,
          transition: theme.transitions.create('padding', { duration: theme.transitions.duration.standard }),
        }}
      >
        <Outlet context={{ userData, onLogout }} />
      </Box>
    </Box>
  );
}

export default AdminLayout;
